/**
 * Primitives géométriques du solveur de figures (F3) — fonction pure, aucune
 * dépendance, partagée client et serveur.
 *
 * Repère de TRAVAIL : y vers le HAUT (convention mathématique). La conversion
 * vers le repère d'affichage (y vers le bas, origine en haut à gauche) est
 * faite une seule fois, à la fin, par `resoudre.ts`.
 */
export const vec = (x, y) => ({ x, y });
export const plus = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
export const moins = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
export const fois = (a, k) => ({ x: a.x * k, y: a.y * k });
export const norme = (a) => Math.hypot(a.x, a.y);
export const milieu = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
/** Vecteur unitaire, ou `null` si le vecteur est nul (longueur non représentable). */
export function unitaire(a) {
    const n = norme(a);
    if (!Number.isFinite(n) || n < 1e-9)
        return null;
    return { x: a.x / n, y: a.y / n };
}
/** Normale gauche (rotation de +90°). */
export const normale = (a) => ({ x: -a.y, y: a.x });
export const rad = (deg) => (deg * Math.PI) / 180;
export const deg = (r) => (r * 180) / Math.PI;
/** Point du cercle de centre `c` et de rayon `r` à l'angle `a` (degrés, sens trigonométrique). */
export const surCercle = (c, r, a) => ({
    x: c.x + r * Math.cos(rad(a)),
    y: c.y + r * Math.sin(rad(a)),
});
/** Rotation de `p` autour de `centre`, en degrés, sens trigonométrique. */
export function tourner(p, centre, degres) {
    if (degres === 0)
        return p;
    const c = Math.cos(rad(degres));
    const s = Math.sin(rad(degres));
    const dx = p.x - centre.x;
    const dy = p.y - centre.y;
    return { x: centre.x + dx * c - dy * s, y: centre.y + dx * s + dy * c };
}
/** Barycentre d'un nuage de points ; `null` si le nuage est vide. */
export function barycentre(points) {
    if (points.length === 0)
        return null;
    let sx = 0;
    let sy = 0;
    for (const p of points) {
        sx += p.x;
        sy += p.y;
    }
    return { x: sx / points.length, y: sy / points.length };
}
/** Projeté orthogonal de `p` sur la droite (a, b) ; `null` si a et b sont confondus. */
export function projete(p, a, b) {
    const ab = moins(b, a);
    const d2 = ab.x * ab.x + ab.y * ab.y;
    if (d2 < 1e-12)
        return null;
    const t = ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / d2;
    return { x: a.x + ab.x * t, y: a.y + ab.y * t };
}
/** Angle du vecteur, en degrés dans [0, 360). */
export function angleDe(v) {
    const a = deg(Math.atan2(v.y, v.x));
    return a < 0 ? a + 360 : a;
}
/** Tous les nombres sont finis ? Garde-fou du solveur : `NaN` ne doit jamais atteindre un tracé. */
export function tousFinis(...valeurs) {
    return valeurs.every((v) => Number.isFinite(v));
}
/**
 * Formate une mesure CALCULÉE, jamais écrite par le modèle (§3.3.2).
 * Deux décimales au maximum, séparateur décimal français, et **jamais
 * arrondie à zéro** : une longueur non nulle qui s'afficherait « 0 » serait un
 * énoncé faux.
 */
export function formaterNombre(valeur) {
    if (!Number.isFinite(valeur))
        return '';
    for (let decimales = 2; decimales <= 6; decimales += 1) {
        const arrondi = Number(valeur.toFixed(decimales));
        if (arrondi !== 0 || valeur === 0) {
            return String(arrondi).replace('.', ',');
        }
    }
    /* Sous le millionième, c'est un résidu de calcul (4,4e-16), pas un nombre à lire : 0. */
    return '0';
}
/* ================================================ l'atelier graphique (doc 26) */
/*
 * Intersections, transformations et distances de l'atelier graphique
 * (`docs/cadre-pedagogique/26-ATELIER-GRAPHIQUE.md`, § 1.4). Même repère de
 * travail (y vers le haut), mêmes garde-fous : une construction sans solution
 * rend `null`, jamais un `NaN`.
 */
/** Seuil sous lequel deux points sont confondus, et deux directions parallèles. */
const EPSILON = 1e-9;
export const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
export const scalaire = (a, b) => a.x * b.x + a.y * b.y;
/** Composante z du produit vectoriel : positive si `b` est à gauche de `a`. */
export const vectoriel = (a, b) => a.x * b.y - a.y * b.x;
/**
 * Deux solutions dans l'ordre de l'atelier : la plus petite abscisse d'abord,
 * puis la plus petite ordonnée. C'est ce qui donne un sens stable à la
 * « branche » 0 ou 1 d'une intersection, quand la figure bouge.
 */
function ranger(p, q) {
    if (Math.abs(p.x - q.x) > EPSILON)
        return p.x < q.x ? [p, q] : [q, p];
    return p.y <= q.y ? [p, q] : [q, p];
}
/** Les droites (a1 b1) et (a2 b2) se coupent en un point ; `null` si elles sont parallèles, confondues ou dégénérées. */
export function intersectionDroites(a1, b1, a2, b2) {
    const u = moins(b1, a1);
    const v = moins(b2, a2);
    const nu = norme(u);
    const nv = norme(v);
    if (nu < EPSILON || nv < EPSILON)
        return null;
    const d = vectoriel(u, v);
    if (Math.abs(d) < EPSILON * nu * nv)
        return null;
    const t = vectoriel(moins(a2, a1), v) / d;
    const p = plus(a1, fois(u, t));
    return tousFinis(p.x, p.y) ? p : null;
}
/**
 * La droite (a b) et le cercle (c, r) : deux points rangés (`ranger`), égaux
 * quand la droite est tangente ; `null` quand elle passe à côté.
 */
export function intersectionDroiteCercle(a, b, c, r) {
    const h = projete(c, a, b);
    if (h === null || !(r > 0))
        return null;
    const d = distance(c, h);
    const u = unitaire(moins(b, a));
    if (u === null)
        return null;
    const reste = r * r - d * d;
    if (reste < -EPSILON * Math.max(1, r * r))
        return null;
    const demi = Math.sqrt(Math.max(0, reste));
    return ranger(plus(h, fois(u, -demi)), plus(h, fois(u, demi)));
}
/**
 * Les cercles (c1, r1) et (c2, r2) : deux points rangés, égaux quand ils sont
 * tangents ; `null` quand ils ne se coupent pas ou sont concentriques.
 */
export function intersectionCercles(c1, r1, c2, r2) {
    if (!(r1 > 0) || !(r2 > 0))
        return null;
    const d = distance(c1, c2);
    if (d < EPSILON)
        return null;
    const tol = EPSILON * Math.max(1, r1 + r2);
    if (d > r1 + r2 + tol || d < Math.abs(r1 - r2) - tol)
        return null;
    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
    const u = fois(moins(c2, c1), 1 / d);
    const m = plus(c1, fois(u, a));
    const n = normale(u);
    return ranger(plus(m, fois(n, h)), plus(m, fois(n, -h)));
}
/** L'image de `p` par la réflexion d'axe (a b) ; `null` si a et b sont confondus. */
export function refleter(p, a, b) {
    const h = projete(p, a, b);
    return h === null ? null : { x: 2 * h.x - p.x, y: 2 * h.y - p.y };
}
/** L'image de `p` par l'homothétie de centre `c` et de rapport `k`. */
export const homothetie = (p, c, k) => plus(c, fois(moins(p, c), k));
/** L'image de `p` par la translation de vecteur `u`. */
export const translater = (p, u) => plus(p, u);
/**
 * Le point de la ligne le plus proche de `p`, son paramètre `t` (a + t·(b − a),
 * borné à [0, 1] pour un segment, à [0, +∞) pour une demi-droite) et sa
 * distance à `p`. Une ligne dégénérée (a = b) se réduit au point a.
 */
export function plusProcheSurLigne(p, a, b, sorte = 'droite') {
    const ab = moins(b, a);
    const d2 = scalaire(ab, ab);
    if (d2 < EPSILON * EPSILON)
        return { q: a, t: 0, d: distance(p, a) };
    let t = scalaire(moins(p, a), ab) / d2;
    if (sorte === 'segment')
        t = Math.min(1, Math.max(0, t));
    else if (sorte === 'demi_droite')
        t = Math.max(0, t);
    const q = plus(a, fois(ab, t));
    return { q, t, d: distance(p, q) };
}
/** Distance de `p` à une ligne (droite par défaut). */
export function distancePointLigne(p, a, b, sorte = 'droite') {
    return plusProcheSurLigne(p, a, b, sorte).d;
}
/** Distance de `p` au cercle (c, r) : |pc − r|. */
export const distancePointCercle = (p, c, r) => Math.abs(distance(p, c) - r);
/** Échantillons d'une courbe : ceux de `tronconsFonction` (`figure-v2.ts`). */
export const ECHANTILLONS_COURBE = 240;
/**
 * Distance de `p` à la courbe y = g(x) sur [x0, x1], par échantillonnage : la
 * courbe est suivie comme la polyligne de ses `echantillons` + 1 points, et
 * les trous (valeurs non finies) coupent la polyligne. `null` : aucune
 * portion finie.
 */
export function distancePointCourbe(p, g, x0, x1, echantillons = ECHANTILLONS_COURBE) {
    if (!tousFinis(x0, x1) || !(x1 > x0) || !(echantillons >= 1))
        return null;
    let meilleur = null;
    let precedent = null;
    for (let i = 0; i <= echantillons; i += 1) {
        const x = x0 + ((x1 - x0) * i) / echantillons;
        const y = g(x);
        if (!Number.isFinite(y)) {
            precedent = null;
            continue;
        }
        const courant = { x, y };
        const r = precedent === null
            ? { q: courant, t: 0, d: distance(p, courant) }
            : plusProcheSurLigne(p, precedent, courant, 'segment');
        if (meilleur === null || r.d < meilleur.d)
            meilleur = { q: r.q, x: r.q.x, d: r.d };
        precedent = courant;
    }
    return meilleur;
}
//# sourceMappingURL=geometrie.js.map